import type { Word } from '../../types/word.types';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Volume2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WordPopoverProps {
  word: Word;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

function WordPopover({ word, children, side = 'top', align = 'center' }: WordPopoverProps) {
  // Get the phonetic from the word data (if available from the API)
  const phonetic = word.phonetic;

  // Get audio pronunciation (if available)
  const audioUrl = word.phonetics?.[0]?.audio;

  const playAudio = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play().catch(console.error);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-96 max-w-[90vw]" side={side} align={align}>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {/* Header */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <h4 className="font-bold text-xl">{word.word}</h4>
                {phonetic && (
                  <span className="text-sm text-muted-foreground font-mono">
                    {phonetic}
                  </span>
                )}
                {audioUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={playAudio}
                    className="h-6 w-6 p-0"
                  >
                    <Volume2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Definitions */}
          <div className="space-y-4">
            {word.definitions.map((definition, index) => (
              <div key={index} className="border-l-2 border-primary/20 pl-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded capitalize">
                    {definition.partOfSpeech}
                  </span>
                </div>

                <p className="text-sm leading-relaxed mb-2">
                  {definition.meaning}
                </p>

                {definition.example && (
                  <div className="text-xs text-muted-foreground italic bg-muted/50 p-2 rounded mb-2">
                    "{definition.example}"
                  </div>
                )}

                {/* Synonyms */}
                {definition.synonyms && definition.synonyms.length > 0 && (
                  <div className="text-xs text-muted-foreground mb-1">
                    <span className="font-medium">Synonyms:</span>{' '}
                    {definition.synonyms.slice(0, 3).join(', ')}
                    {definition.synonyms.length > 3 && '...'}
                  </div>
                )}

                {/* Antonyms */}
                {definition.antonyms && definition.antonyms.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">Antonyms:</span>{' '}
                    {definition.antonyms.slice(0, 3).join(', ')}
                    {definition.antonyms.length > 3 && '...'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Source */}
          {word.sourceUrls && word.sourceUrls.length > 0 && (
            <div className="pt-2 border-t">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <ExternalLink className="h-3 w-3" />
                <a
                  href={word.sourceUrls[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  Source
                </a>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default WordPopover;